import React, { useState } from 'react';
import { Table, Button, Spinner, Modal, Form } from 'react-bootstrap';
import { useQuery, useQueryClient } from 'react-query';
import { Get, Put } from '@app/utils/httpProvider';
import { ACCOUNT_AUTHINFO_API, ACCOUNT_API, BaseGatewayImage, DANHSACH_DUYET_NGOAINGU_API } from '@app/utils/apiUrl';
import { toast } from 'react-toastify';
import { getUser } from '@app/utils/oidc';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';

const DuyetVanBangChungChi = () => {
    const queryClient = useQueryClient();
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    // State cho modal xác nhận duyệt
    const [openConfirmDuyet, setOpenConfirmDuyet] = useState(false);
    const [openReasonModal, setOpenReasonModal] = useState(false);
    const [reasonText, setReasonText] = useState('');
    const [confirmAction, setConfirmAction] = useState(null); // { item, field, value, label }

    // Hàm fetch dữ liệu và nhóm theo AccountId
    const fetchAllData = async () => {
        try {
            const params = {
                pageIndex: currentPage,
                pageSize: itemsPerPage,
                keyword: "",
            };

            const [vanBangRes, chungChiRes] = await Promise.all([
                Get(DANHSACH_DUYET_NGOAINGU_API.GET_ALL_VANBANG_BY_PAGING, null, params),
                Get(DANHSACH_DUYET_NGOAINGU_API.GET_ALL_CHUNGCHINGOAINGU_BY_PAGING, null, params)
            ]);

            const vanBangItems = (vanBangRes?.data?.$values || vanBangRes?.data?.data || (Array.isArray(vanBangRes?.data) ? vanBangRes.data : [])).map(item => ({ ...item, type: 'vanBang' }));
            const chungChiItems = (chungChiRes?.data?.$values || chungChiRes?.data?.data || (Array.isArray(chungChiRes?.data) ? chungChiRes.data : [])).map(item => ({ ...item, type: 'chungChi' }));

            // Gộp và lọc bỏ Nhập liệu
            const combinedItems = [...vanBangItems, ...chungChiItems].filter(item => {
                const status = item.trangThai ?? item.TrangThai;
                if (status === null || status === undefined) return true;
                if (!isNaN(status)) return Number(status) !== 0;
                return status !== "NhapLieu";
            });

            // Nhóm theo AccountId
            const groupedMap = {};
            combinedItems.forEach(item => {
                const accId = item.accountId || item.AccountId;
                if (!groupedMap[accId]) {
                    groupedMap[accId] = {
                        accountId: accId,
                        items: []
                    };
                }
                groupedMap[accId].items.push(item);
            });

            const uniqueAccIds = Object.keys(groupedMap);

            // Fetch profile cho từng AccountId duy nhất
            const profiles = await Promise.all(uniqueAccIds.map(async (accId) => {
                try {
                    const [authRes, detailRes] = await Promise.all([
                        Get(`${ACCOUNT_AUTHINFO_API.GET_BY_ID_ACCOUNT}/${accId}`),
                        Get(`${ACCOUNT_API.GET_ACCOUNT_INFO_BYID_FROM_APP_DUNGCHUNG}/${accId}`)
                    ]);

                    const auth = authRes?.data || authRes;
                    const detail = detailRes?.data || detailRes;

                    const fullName = auth ? [
                        auth.lastName || auth.LastName,
                        auth.middleName || auth.MiddleName,
                        auth.firstName || auth.FirstName
                    ].filter(v => v && String(v).trim() !== "").join(' ') : '-';

                    const dob = auth?.dateOfBirth || auth?.DateOfBirth;
                    const yearOfBirth = dob ? new Date(dob).getFullYear() : '-';
                    const chucDanh = detail?.doiTuong?.tenDoiTuong || detail?.DoiTuong?.TenDoiTuong || '-';
                    const donVis = detail?.donVis || detail?.DonVis?.$values || detail?.DonVis || [];
                    const khoa = (Array.isArray(donVis) && donVis[0]) ? (donVis[0].tenDonVi || donVis[0].TenDonVi) : '-';

                    return { accId, fullName, yearOfBirth, chucDanh, khoa };
                } catch (e) {
                    return { accId, fullName: '-', yearOfBirth: '-', chucDanh: '-', khoa: '-' };
                }
            }));

            // Trộn data profile vào Map
            profiles.forEach(p => {
                if (groupedMap[p.accId]) {
                    groupedMap[p.accId] = { ...groupedMap[p.accId], ...p };
                }
            });

            return Object.values(groupedMap);
        } catch (error) {
            console.error('Lỗi lấy dữ liệu duyệt:', error);
            throw error;
        }
    };

    const { data: listData, isLoading, refetch } = useQuery(
        ['duyetVanBangChungChi', currentPage],
        fetchAllData,
        { cacheTime: 0, staleTime: 0 }
    );

    const handleUpdateStatus = async (item, field, value) => {
        const queryKey = ['duyetVanBangChungChi', currentPage];
        const itemId = item.id || item.Id || item.vanBangId || item.chungChiNgoaiNguId;

        // Optimistic Update trong cấu trúc dữ liệu mới (nested)
        queryClient.setQueryData(queryKey, (oldData) => {
            if (!oldData) return [];
            return oldData.map((employee) => ({
                ...employee,
                items: employee.items.map((it) => {
                    const itId = it.id || it.Id || it.vanBangId || it.chungChiNgoaiNguId;
                    if (itId === itemId) {
                        return { 
                            ...it, 
                            [field]: value,
                            [field.charAt(0).toLowerCase() + field.slice(1)]: value 
                        };
                    }
                    return it;
                })
            }));
        });

        try {
            const apiPath = item.type === 'vanBang' ? DANHSACH_DUYET_NGOAINGU_API.UPDATE_FIELD_VAN_BANG : DANHSACH_DUYET_NGOAINGU_API.UPDATE_FIELD_CHUNG_CHI_NGOAI_NGU;
            const payload = { id: itemId, fieldName: field, value: value };

            const res = await Put(apiPath, null, payload);
            if (res) {
                toast.success('Cập nhật trạng thái thành công');
                queryClient.invalidateQueries(queryKey);
                queryClient.invalidateQueries(['thongKeNgoaiNgu']);
            }
        } catch (error) {
            toast.error('Lỗi cập nhật trạng thái');
            refetch();
        }
    };

    const openConfirmAction = (item, field, value, label) => {
        setConfirmAction({ item, field, value, label });
        // Nếu là Không duyệt (TrangThai = 3) hoặc Không đạt (XacNhanDatYeuCau = 'Không đạt') thì mở Modal lý do
        if ((field === 'TrangThai' && value === 3) || (field === 'XacNhanDatYeuCau' && value === 'Không đạt')) {
            setReasonText('');
            setOpenReasonModal(true);
        } else {
            setOpenConfirmDuyet(true);
        }
    };

    const handleReasonSubmit = async () => {
        if (!reasonText.trim()) {
            toast.error('Vui lòng nhập lý do không phù hợp');
            return;
        }
        if (!confirmAction) return;

        const { item, field, value } = confirmAction;
        setOpenReasonModal(false);

        // Gọi cập nhật trạng thái trước
        await handleUpdateStatus(item, field, value);

        // Sau đó gọi cập nhật field lý do tương ứng (LyDoKhongDuyet hoặc LyDoKhongDat)
        try {
            const apiPath = item.type === 'vanBang' ? DANHSACH_DUYET_NGOAINGU_API.UPDATE_FIELD_VAN_BANG : DANHSACH_DUYET_NGOAINGU_API.UPDATE_FIELD_CHUNG_CHI_NGOAI_NGU;
            const itemId = item.id || item.Id || item.vanBangId || item.chungChiNgoaiNguId;
            
            const reasonFieldName = (field === 'TrangThai') ? 'LyDoKhongDuyet' : 'LyDoKhongDat';
            
            const payload = { id: itemId, fieldName: reasonFieldName, value: reasonText };
            
            await Put(apiPath, null, payload);
        } catch (error) {
            console.error('Lỗi cập nhật lý do:', error);
        }

        setConfirmAction(null);
    };

    const handleConfirmAccept = async () => {
        if (!confirmAction) return;
        const { item, field, value } = confirmAction;
        setOpenConfirmDuyet(false);

        // Thực hiện cập nhật trạng thái chính
        await handleUpdateStatus(item, field, value);

        // Nếu là Duyệt (TrangThai = 2), reset LyDoKhongDuyet về null
        // Nếu là Đạt (XacNhanDatYeuCau = 'Đạt'), reset LyDoKhongDat về null
        if ((field === 'TrangThai' && value === 2) || (field === 'XacNhanDatYeuCau' && value === 'Đạt')) {
            try {
                const apiPath = item.type === 'vanBang' ? DANHSACH_DUYET_NGOAINGU_API.UPDATE_FIELD_VAN_BANG : DANHSACH_DUYET_NGOAINGU_API.UPDATE_FIELD_CHUNG_CHI_NGOAI_NGU;
                const itemId = item.id || item.Id || item.vanBangId || item.chungChiNgoaiNguId;
                
                const reasonFieldName = (field === 'TrangThai') ? 'LyDoKhongDuyet' : 'LyDoKhongDat';
                
                const payload = { id: itemId, fieldName: reasonFieldName, value: null };
                await Put(apiPath, null, payload);
            } catch (error) {
                console.error('Lỗi reset lý do:', error);
            }
        }

        setConfirmAction(null);
    };

    const handleConfirmCancel = () => {
        setOpenConfirmDuyet(false);
        setConfirmAction(null);
    };

    const handleOpenFile = (path) => {
        if (!path) return;
        const fullPath = path.startsWith('http') ? path : `${BaseGatewayImage}${path}`;
        window.open(fullPath, '_blank');
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Đang tải dữ liệu và thông tin nhân viên...</p>
            </div>
        );
    }

    return (
        <div className="py-2">
            <div className="table-responsive">
                <Table bordered className="align-middle custom-approval-table">
                    <thead className="table-light">
                        <tr className="text-center text-nowrap">
                            <th style={{ width: '50px' }}>STT</th>
                            <th style={{ minWidth: '180px' }}>Họ và tên</th>
                            <th style={{ width: '100px' }}>Năm sinh</th>
                            <th style={{ minWidth: '110px' }}>Chức danh</th>
                            <th style={{ minWidth: '180px' }}>Đơn vị</th>
                            <th style={{ minWidth: '400px' }}>Văn bằng chứng chỉ ngoại ngữ</th>
                            <th style={{ minWidth: '140px' }}>Xác nhận Đạt yêu cầu</th>
                        </tr>
                    </thead>
                    <tbody>
                        {listData?.length > 0 ? listData.map((employee, index) => {
                            const stt = (currentPage - 1) * itemsPerPage + index + 1;
                            const rowSpan = employee.items.length;

                            return (
                                <React.Fragment key={employee.accountId || index}>
                                    {employee.items.map((item, idx) => {
                                        const tenHienThi = item.tenVanBang || item.TenVanBang || item.tenChungChi || item.TenChungChi || '-';
                                        const filePath = item.fileMinhChungPath || item.FileMinhChungPath;
                                        
                                        const parseTrangThai = (val) => {
                                            if (val === null || val === undefined) return -1;
                                            if (!isNaN(val)) return Number(val);
                                            const enumMap = { "NhapLieu": 0, "ChoDuyet": 1, "DaDuyet": 2, "TuChoi": 3 };
                                            return enumMap[String(val).trim()] ?? -1;
                                        };

                                        const statusDuyet = parseTrangThai(item.trangThai ?? item.TrangThai);
                                        const statusDat = item.xacNhanDatYeuCau ?? item.XacNhanDatYeuCau ?? null;

                                        return (
                                            <tr key={item.id || `${employee.accId}-${idx}`}>
                                                {idx === 0 && (
                                                    <>
                                                        <td rowSpan={rowSpan} className="text-center" style={{ color: '#000' }}>{stt}</td>
                                                        <td rowSpan={rowSpan} className="fw-bold text-primary" style={{ color: '#007bff' }}>{employee.fullName || '-'}</td>
                                                        <td rowSpan={rowSpan} className="text-center" style={{ color: '#000' }}>{employee.yearOfBirth || '-'}</td>
                                                        <td rowSpan={rowSpan} style={{ color: '#000' }}>{employee.chucDanh || '-'}</td>
                                                        <td rowSpan={rowSpan} style={{ color: '#000' }}>{employee.khoa || '-'}</td>
                                                    </>
                                                )}
                                                <td style={{ color: '#000' }}>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                                                            <a href="#" className="fw-bold d-flex align-items-center"
                                                                onClick={(e) => { e.preventDefault(); handleOpenFile(filePath); }}
                                                                style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.95rem' }}>
                                                                {tenHienThi} <i className="fas fa-external-link-alt ms-2" style={{ fontSize: '0.8rem' }}></i>
                                                            </a>
                                                        </div>
                                                        <div className="d-flex gap-2 ms-2">
                                                            <Button variant={statusDuyet === 2 ? 'success' : 'outline-success'} size="sm" className="btn-icon-square"
                                                                onClick={() => openConfirmAction(item, 'TrangThai', 2, `Xác nhận duyệt "${tenHienThi}"?`)} title="Duyệt">
                                                                <i className="fas fa-check"></i>
                                                            </Button>
                                                            <Button variant={statusDuyet === 3 ? 'danger' : 'outline-danger'} size="sm" className="btn-icon-square"
                                                                onClick={() => openConfirmAction(item, 'TrangThai', 3, `Xác nhận không duyệt "${tenHienThi}"?`)} title="Không duyệt">
                                                                <i className="fas fa-times"></i>
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="text-center" style={{ color: '#000' }}>
                                                    <div className="d-flex justify-content-center gap-2">
                                                        <Button variant={(statusDat === 'Đạt') && statusDat !== null ? 'success' : 'outline-success'} size="sm" className="btn-icon-square"
                                                            onClick={() => openConfirmAction(item, 'XacNhanDatYeuCau', 'Đạt', 'Xác nhận đạt yêu cầu?')} title="Đạt">
                                                            <i className="fas fa-check"></i>
                                                        </Button>
                                                        <Button variant={(statusDat === 'Không đạt') && statusDat !== null ? 'danger' : 'outline-danger'} size="sm" className="btn-icon-square"
                                                            onClick={() => openConfirmAction(item, 'XacNhanDatYeuCau', 'Không đạt', 'Xác nhận không đạt yêu cầu?')} title="Không đạt">
                                                            <i className="fas fa-times"></i>
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        }) : (
                            <tr><td colSpan="7" className="text-center py-4 text-muted">Không có dữ liệu chờ duyệt</td></tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <ConfirmModal isOpen={openConfirmDuyet} confirmType="confirm" handleAccept={handleConfirmAccept} handleCancel={handleConfirmCancel}
                content={confirmAction?.label || 'Bạn có chắc chắn muốn thực hiện thao tác này?'} />

            {/* Modal Lý do không phù hợp */}
            <Modal 
                show={openReasonModal} 
                onHide={() => setOpenReasonModal(false)} 
                centered 
                className="reason-modal"
                style={{ zIndex: 1060 }}
                backdrop="static"
            >
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="d-flex align-items-center text-danger fw-bold h5">
                        <i className="fas fa-exclamation-triangle me-2"></i> Lý do không phù hợp
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <Form.Group>
                        <Form.Label className="fw-bold">Chọn lý do <span className="text-danger">*</span></Form.Label>
                        <Form.Control 
                            as="input" 
                            placeholder="-- Vui lòng nhập lý do --" 
                            value={reasonText}
                            onChange={(e) => setReasonText(e.target.value)}
                            className="py-2"
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0 justify-content-end pt-0">
                    <Button variant="light" onClick={() => setOpenReasonModal(false)} className="px-4 border">
                        Hủy
                    </Button>
                    <Button variant="primary" onClick={handleReasonSubmit} className="px-4 bg-primary border-0" style={{ background: '#58a5ff' }}>
                        Tiếp tục
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .custom-approval-table { border: 1px solid #dee2e6; font-size: 0.9rem; }
                .custom-approval-table thead th { background-color: #f8f9fa; color: #495057; font-weight: 700; border: 1px solid #dee2e6 !important; border-bottom: 2px solid #dee2e6 !important; padding: 12px 8px; text-align: center; }
                .custom-approval-table tbody td { padding: 12px 8px; vertical-align: middle; text-align: center; background-color: #fff; border: 1px solid #dee2e6; }
                .btn-icon-square { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; padding: 0; border-radius: 3px; }
                
                .reason-modal .modal-content { border-radius: 12px; border: none; box-shadow: 0 5px 15px rgba(0,0,0,.2); }
                .reason-modal .modal-header .btn-close { font-size: 0.8rem; }
                .reason-modal .form-control:focus { box-shadow: none; border-color: #dee2e6; }
                
                /* Đảm bảo modal luôn nằm trên cùng */
                .modal-open .modal { z-index: 1060 !important; }
                .modal-backdrop { z-index: 1050 !important; }
            `}</style>
        </div>
    );
};

export default DuyetVanBangChungChi;